import * as React from 'react';
import Page from '../../components/Page';
import { ButtonStyle } from '../../components/Button';
import { ActionBar, ActionBarStyle } from '../../components/ActionBar';
import { Product, ProductDetail } from '../../product';
import { ErrorFlash, SuccessFlash } from '../../components/Flash';
import { ProfileContext, ProfileManager } from '../../profile';
import { useRequestAsync, getHeaders } from '../../api/client';
import GeneralProductInfo from './GeneralProductInfo';
import ProductAvailability from './ProductAvailability';
import ProductDetailsInfo from './ProductDetailsInfo';
import { Link, Redirect } from 'react-router-dom';
import ProductImagery from './ProductImagery';
import ProductAppearance from './ProductAppearance';
import Loader from '../../components/Loader';

const JumpLink = (props: any) => (
  <button
    type="button"
    className={`f5 link dim pv3 pa0 db black bn bg-transparent outline-0 tl ${
      props.current === props.href ? 'b' : 'normal'
    }`}
    onClick={() => props.onClick(props.href)}
  >
    {props.children}
  </button>
);

type Props = {
  product: Product;
};

const update = (context: ProfileManager) =>
  useRequestAsync((data: Product) => ({
    url: data.id ? `/store/products/${data.id}` : '/store/products/new',
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

const deleteProduct = (context: ProfileManager) =>
  useRequestAsync((data: Product) => ({
    url: `/store/products/${data.id}`,
    method: 'DELETE',
    body: JSON.stringify({}),
    headers: getHeaders(context),
  }));

export default function EditProduct(props: Props) {
  const context = React.useContext(ProfileContext);
  const [product, setProduct] = React.useState(props.product);
  const [section, setSection] = React.useState('general');
  const [state, submit] = update(context);
  const [deleteState, submitDelete] = deleteProduct(context);

  React.useEffect(() => {
    if (state.complete) {
      window.scrollTo(0, 0);
    }
  }, [state]);

  const handleFormSubmit = () => {
    const data = Object.assign({}, product, {
      details: product.details.filter(
        (item: ProductDetail) => item.name && item.value,
      ),
    });

    submit(data);
  };
  const handlerForDelete = () => submitDelete(product);

  if (state.loading || deleteState.loading) {
    return <Loader />;
  }

  if (deleteState.data && deleteState.complete) {
    return <Redirect to="/products" />;
  }

  return (
    <Page title={product.name || 'A new product'} subtitle="Edit product">
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && state.data && (
        <SuccessFlash text="Product has been saved" />
      )}
      <div className="flex justify-between">
        <div className="w-20 flex flex-column">
          <Link to="/products" className="f5 link dim pv3 b black">
            ← Back to Products
          </Link>
          <JumpLink onClick={setSection} href="general" current={section}>
            General
          </JumpLink>
          <JumpLink onClick={setSection} href="availability" current={section}>
            Availability
          </JumpLink>
          <JumpLink onClick={setSection} href="details" current={section}>
            Product Details
          </JumpLink>
          <JumpLink onClick={setSection} href="imagery" current={section}>
            Images
          </JumpLink>
          <JumpLink onClick={setSection} href="appearance" current={section}>
            Appearance
          </JumpLink>
        </div>
        <div className="w-80 flex-auto">
          <form onSubmit={handleFormSubmit} id="product">
            {section === 'general' && (
              <GeneralProductInfo product={product} setProduct={setProduct} />
            )}
            {section === 'availability' && (
              <ProductAvailability product={product} setProduct={setProduct} />
            )}
            {section === 'details' && (
              <ProductDetailsInfo product={product} setProduct={setProduct} />
            )}
            {section === 'imagery' && (
              <ProductImagery product={product} setProduct={setProduct} />
            )}
            {section === 'appearance' && (
              <ProductAppearance product={product} setProduct={setProduct} />
            )}
            <ActionBar
              className={ActionBarStyle.alignCenter}
              actions={[
                {
                  className: ButtonStyle.Confirm,
                  label: 'Save',
                  handler: handleFormSubmit,
                },
                {
                  className: ButtonStyle.Danger,
                  label: 'Delete',
                  handler: handlerForDelete,
                },
              ]}
            />
          </form>
        </div>
      </div>
    </Page>
  );
}
